import { CARD_IDS, getCard, tenantNames, type GroupId } from '@lafayette/content';
import type { ActionTargets, CommandBody, GameProjection } from '@lafayette/protocol';
import { Card, describeGroups, GroupChip } from '@lafayette/ui';
import { useEffect, useState, type CSSProperties } from 'react';

import styles from './Controls.module.css';
import { debtSentence, type PaymentPlan } from './payment.js';
import { legalOfType, nameOf, titleOf, valueOf } from './selectors.js';
import { targetStepFor, type ActionDraft } from './targeting.js';

export interface ControlsProps {
  projection: GameProjection;
  send: (body: CommandBody) => void;
  selectedHandCard: string | null;
  onSelectHandCard: (instanceId: string | null) => void;
  onClearPayment: () => void;
  discardSelection: string[];
  onClearDiscard: () => void;
  /** Set while this player owes a debt; cards are staged on the table. */
  plan: PaymentPlan | null;
  /** The half-built action. Lives in the table so cards can be picked on it. */
  draft: ActionDraft | null;
  onDraftChange: (draft: ActionDraft | null) => void;
  onConfirmAction: (draft: ActionDraft) => void;
  drawProgress: number;
  endTurnProgress: number;
  onDrawForTurn: () => void;
  onEndTurn: () => void;
}

/**
 * The only place a command is issued. Every button here is derived from the
 * engine's `legalCommands`, so the UI can never offer an illegal move.
 */
export function Controls(props: ControlsProps) {
  const { projection, send } = props;
  const draft = props.draft;
  const setDraft = props.onDraftChange;
  const [tenantName, setTenantName] = useState('');
  const [colorChoice, setColorChoice] = useState(false);
  // Renegotiate the Rent chosen alongside a rent card, before it is played.
  const [doubles, setDoubles] = useState<string[]>([]);

  // Any state change from the server invalidates a half-built color choice.
  useEffect(() => {
    setColorChoice(false);
  }, [projection.version]);

  // Picking a different card starts its options over.
  useEffect(() => {
    setDoubles([]);
  }, [props.selectedHandCard]);

  if (projection.winnerId) {
    return (
      <Panel title="Match over">
        <p className={styles.note}>
          {projection.winnerId === projection.self.id
            ? 'You leased three strip malls first.'
            : `${nameOf(projection, projection.winnerId)} leased three strip malls first.`}
        </p>
      </Panel>
    );
  }

  const respond = legalOfType(projection, 'respondToAction')[0];
  if (respond && projection.pendingAction) {
    const card = getCard(projection.pendingAction.definitionId);
    const request = pendingRequestDetails(projection);
    return (
      <Panel title="Respond">
        <p className={styles.note}>
          {nameOf(projection, projection.pendingAction.actorId)} played <b>{card.title}</b> against
          you
          {projection.pendingAction.amount ? ` for $${projection.pendingAction.amount}M` : ''}.
        </p>
        {request ? <PendingRequestSummary projection={projection} request={request} /> : null}
        <div className={styles.row}>
          <button
            type="button"
            className="lf-button lf-button--primary"
            disabled={respond.cancelCardInstanceIds.length === 0}
            onClick={() =>
              send({
                type: 'respondToAction',
                cancel: true,
                cardInstanceId: respond.cancelCardInstanceIds[0],
              })
            }
          >
            Blame the Investment Committee
          </button>
          <button
            type="button"
            className="lf-button"
            onClick={() => send({ type: 'respondToAction', cancel: false })}
          >
            Let it stand
          </button>
        </div>
      </Panel>
    );
  }

  const plan = props.plan;
  if (plan) {
    return (
      <Panel title={`Pay $${plan.amount}M`}>
        <p className={styles.note}>
          {debtSentence(projection, plan)}{' '}
          {plan.mustGiveEverything
            ? `You only have $${plan.available}M on the table, so hand over everything you can.`
            : 'Pick cards from your own table and bank. Cards in your hand can never pay.'}
        </p>
        <p
          className={`${styles.tally} ${plan.ready ? styles.tallyReady : ''}`}
          data-testid="payment-total"
          role="status"
        >
          Selected ${plan.total}M of ${plan.amount}M
        </p>
        <div className={styles.row}>
          <button
            type="button"
            className="lf-button lf-button--primary"
            data-testid="confirm-payment"
            disabled={!plan.ready}
            onClick={() => {
              send({ type: 'payDebt', debtId: plan.debtId, cardInstanceIds: plan.selected });
              props.onClearPayment();
            }}
          >
            Pay ${plan.total}M
          </button>
          {plan.mustGiveEverything ? (
            <button
              type="button"
              className="lf-button"
              onClick={() =>
                send({
                  type: 'payDebt',
                  debtId: plan.debtId,
                  cardInstanceIds: plan.eligible.map((asset) => asset.instanceId),
                })
              }
            >
              Hand over everything
            </button>
          ) : null}
          <button
            type="button"
            className="lf-button lf-button--ghost"
            disabled={plan.selected.length === 0}
            onClick={props.onClearPayment}
          >
            Clear
          </button>
        </div>
      </Panel>
    );
  }

  const upgrade = legalOfType(projection, 'placeUpgrade')[0];
  if (upgrade) {
    return (
      <Panel title="Place the upgrade">
        <p className={styles.note}>
          You received <b>{titleOf(projection, upgrade.cardInstanceId)}</b>. Attach it to a complete
          mall for free, or bank it as cash.
        </p>
        <div className={styles.row}>
          {upgrade.groups.map((group) => (
            <button
              key={group}
              type="button"
              className="lf-button lf-button--primary"
              onClick={() =>
                send({ type: 'placeUpgrade', cardInstanceId: upgrade.cardInstanceId, group })
              }
              aria-label={`Attach to ${describeGroups([group])}`}
            >
              <span className={styles.inlineWord}>Attach to</span>
              <GroupChip groups={group} />
            </button>
          ))}
          <button
            type="button"
            className="lf-button"
            onClick={() =>
              send({ type: 'placeUpgrade', cardInstanceId: upgrade.cardInstanceId, group: null })
            }
          >
            Bank as cash
          </button>
        </div>
      </Panel>
    );
  }

  const discard = legalOfType(projection, 'discardCards')[0];
  if (discard) {
    const canCancelEndTurn = legalOfType(projection, 'cancelEndTurn').length > 0;
    return (
      <Panel title={`Discard ${discard.count}`}>
        <p className={styles.note}>
          You are over the seven-card limit. Pick {discard.count} card
          {discard.count === 1 ? '' : 's'} from your hand.
        </p>
        <p className={styles.tally}>
          Selected {props.discardSelection.length} of {discard.count}
        </p>
        <div className={styles.row}>
          <button
            type="button"
            className="lf-button lf-button--primary"
            disabled={props.discardSelection.length !== discard.count}
            onClick={() => {
              send({ type: 'discardCards', cardInstanceIds: props.discardSelection });
              props.onClearDiscard();
            }}
          >
            Discard
          </button>
          <button
            type="button"
            className="lf-button lf-button--ghost"
            onClick={props.onClearDiscard}
          >
            Clear
          </button>
          {canCancelEndTurn ? (
            <button
              type="button"
              className="lf-button lf-button--ghost"
              onClick={() => {
                send({ type: 'cancelEndTurn' });
                props.onClearDiscard();
              }}
            >
              Cancel end turn
            </button>
          ) : null}
        </div>
      </Panel>
    );
  }

  const draw = legalOfType(projection, 'drawForTurn')[0];
  if (draw) {
    return (
      <Panel title="Your turn">
        <div className={styles.row}>
          <button
            type="button"
            className={`lf-button ${styles.progressButton}`}
            style={progressStyle(props.drawProgress)}
            data-testid="draw-turn"
            onClick={props.onDrawForTurn}
          >
            <span>{projection.self.hand.length === 0 ? 'Draw 5 cards' : 'Draw 2 cards'}</span>
          </button>
        </div>
        <WildcardMoves projection={projection} send={send} />
      </Panel>
    );
  }

  if (projection.turn.playerId !== projection.self.id) {
    return (
      <Panel title="Not your turn">
        <p className={styles.note}>{nameOf(projection, projection.turn.playerId)} is playing.</p>
      </Panel>
    );
  }

  // -- main phase -----------------------------------------------------------

  if (draft) {
    return (
      <ActionBuilder
        projection={projection}
        draft={draft}
        setDraft={setDraft}
        onCancel={() => setDraft(null)}
        onConfirm={() => props.onConfirmAction(draft)}
      />
    );
  }

  const selected = props.selectedHandCard;
  const bankable = legalOfType(projection, 'bankCard')[0];
  const properties = legalOfType(projection, 'playProperty')[0];
  const actions = legalOfType(projection, 'playAction')[0];
  const actionsLeft = projection.turn.actionsPerTurn - projection.turn.actionsUsed;

  if (selected && actionsLeft > 0) {
    const propertyOption = properties?.options.find((o) => o.cardInstanceId === selected);
    const actionOption = actions?.options.find((o) => o.cardInstanceId === selected);
    const canBank = bankable?.cardInstanceIds.includes(selected) ?? false;
    const definition = getCard(projection.cards[selected]!);
    const isWildcard = definition.kind === 'propertyWildcard';
    const isDoubler = definition.id === CARD_IDS.doubleRent;

    // Renegotiate the Rent rides along with a rent card and costs its own
    // action, so the engine only offers it with two or more left.
    const doublersAvailable = actionOption?.doubleRentInstanceIds ?? [];
    const rentCardsForDoubler = isDoubler
      ? (actions?.options.filter((o) => o.doubleRentInstanceIds.includes(selected)) ?? [])
      : [];
    const playProperty = (group: GroupId): void => {
      send({
        type: 'playProperty',
        cardInstanceId: selected,
        group,
        tenantName: tenantName.trim() || undefined,
      });
      setTenantName('');
      setColorChoice(false);
      props.onSelectHandCard(null);
    };

    // A wildcard has to be told which mall it is filling; a plain property has
    // exactly one home, so it plays in a single click.
    if (colorChoice && propertyOption) {
      return (
        <Panel title="Play wildcard">
          <Chooser label="Fill which mall?">
            {propertyOption.groups.map((group) => (
              <button
                key={group}
                type="button"
                className="lf-button lf-button--small"
                data-play="group"
                onClick={() => playProperty(group)}
                aria-label={`Play into ${describeGroups([group])}`}
              >
                <GroupChip groups={group} />
              </button>
            ))}
          </Chooser>
          <div className={styles.row}>
            <button
              type="button"
              className="lf-button lf-button--ghost"
              onClick={() => setColorChoice(false)}
            >
              Back
            </button>
          </div>
        </Panel>
      );
    }

    return (
      <Panel title={titleOf(projection, selected)}>
        {definition.kind === 'action' || definition.kind === 'rent' ? (
          <p className={styles.note}>{definition.description}</p>
        ) : null}

        {propertyOption?.needsTenantName ? (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="tenant">
              Name your tenant (flavor only)
            </label>
            <div className={styles.inputRow}>
              <input
                id="tenant"
                className="lf-input"
                value={tenantName}
                maxLength={40}
                placeholder="e.g. Drive-thru Coffee Shop"
                onChange={(e) => setTenantName(e.target.value)}
              />
              <button
                type="button"
                className="lf-button lf-button--small"
                onClick={() => setTenantName(randomTenant(tenantName))}
              >
                Choose for me
              </button>
            </div>
          </div>
        ) : null}

        {doublersAvailable.length > 0 ? (
          <div className={styles.subsection}>
            <p className={styles.subheading}>Renegotiate the Rent</p>
            <div className={styles.row}>
              <button
                type="button"
                className={`lf-button lf-button--small ${doubles.length > 0 ? 'lf-button--primary' : ''}`}
                aria-pressed={doubles.length > 0}
                data-testid="toggle-doubler"
                onClick={() => setDoubles(doubles.length > 0 ? [] : [doublersAvailable[0]!])}
              >
                {doubles.length > 0 ? 'Rent doubled — tap to undo' : 'Double this rent (2 actions)'}
              </button>
            </div>
          </div>
        ) : null}

        {isDoubler ? (
          <div className={styles.subsection}>
            <p className={styles.subheading}>Play with a rent card</p>
            {rentCardsForDoubler.length > 0 ? (
              <div className={styles.row}>
                {rentCardsForDoubler.map((option) => {
                  const rent = getCard(option.definitionId);
                  return (
                    <button
                      key={option.cardInstanceId}
                      type="button"
                      className="lf-button lf-button--primary"
                      data-testid="attach-doubler"
                      onClick={() =>
                        setDraft({
                          option,
                          targets: {},
                          doubles: [selected],
                        })
                      }
                    >
                      <span className={styles.inlineWord}>Double</span>
                      {rent.kind === 'rent' ? <GroupChip groups={rent.groups} /> : null}
                      <span className={styles.inlineWord}>{rent.title}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className={styles.note}>
                {actionsLeft < 2
                  ? 'You need two actions left to play this with a rent card.'
                  : 'You have no rent card you could play right now.'}
              </p>
            )}
          </div>
        ) : null}

        <div className={styles.row}>
          {propertyOption ? (
            <button
              type="button"
              className="lf-button lf-button--primary"
              data-play="property"
              onClick={() => {
                if (propertyOption.groups.length === 1) playProperty(propertyOption.groups[0]!);
                else setColorChoice(true);
              }}
            >
              Play{' '}
              {isWildcard ? (
                <>
                  <GroupChip groups={definition.groups} />
                  <span className={styles.inlineWord}>wildcard</span>
                </>
              ) : (
                definition.title
              )}
            </button>
          ) : null}

          {actionOption ? (
            <button
              type="button"
              className="lf-button lf-button--primary"
              data-play="action"
              onClick={() => setDraft({ option: actionOption, targets: {}, doubles })}
            >
              Play {getCard(actionOption.definitionId).title}
            </button>
          ) : null}

          {canBank ? (
            <button
              type="button"
              className="lf-button"
              onClick={() => {
                send({ type: 'bankCard', cardInstanceId: selected });
                props.onSelectHandCard(null);
              }}
            >
              Bank as ${valueOf(projection, selected)}M
            </button>
          ) : null}

          <button
            type="button"
            className="lf-button lf-button--ghost"
            onClick={() => props.onSelectHandCard(null)}
          >
            Cancel
          </button>
        </div>

        {!propertyOption && !actionOption && !canBank ? (
          <p className={styles.note}>Nothing legal to do with this card right now.</p>
        ) : null}
      </Panel>
    );
  }

  return (
    <Panel
      title={`Your turn — ${projection.turn.actionsPerTurn - projection.turn.actionsUsed} action${
        projection.turn.actionsPerTurn - projection.turn.actionsUsed === 1 ? '' : 's'
      } left`}
    >
      <p className={styles.note}>Pick a card from your hand, or end your turn.</p>
      <WildcardMoves projection={projection} send={send} />
      <div className={styles.row}>
        <button
          type="button"
          className={`lf-button ${styles.progressButton}`}
          style={progressStyle(props.endTurnProgress)}
          data-testid="end-turn"
          onClick={props.onEndTurn}
        >
          <span>End turn</span>
        </button>
      </div>
    </Panel>
  );
}

interface PendingRequestDetails {
  requestedIds: string[];
  offeredIds: string[];
  completeMall: boolean;
}

function pendingRequestDetails(projection: GameProjection): PendingRequestDetails | null {
  const pending = projection.pendingAction;
  if (!pending?.affected.includes(projection.self.id)) return null;

  const requested = pending.targets.propertyInstanceId;
  if (requested) {
    const offered = pending.targets.ownPropertyInstanceId;
    return {
      requestedIds: [requested],
      offeredIds: offered ? [offered] : [],
      completeMall: false,
    };
  }

  if (pending.targets.playerId !== projection.self.id || !pending.targets.group) return null;
  const mall = projection.self.malls[pending.targets.group];
  if (!mall) return null;
  return {
    requestedIds: [...mall.cards, ...mall.upgrades],
    offeredIds: [],
    completeMall: true,
  };
}

function PendingRequestSummary({
  projection,
  request,
}: {
  projection: GameProjection;
  request: PendingRequestDetails;
}) {
  const swap = request.offeredIds.length > 0;
  return (
    <div className={styles.requested} data-testid="requested-cards">
      <p className={styles.requestedTitle}>
        {swap
          ? 'They want to swap these cards:'
          : request.completeMall
            ? 'They want to take this complete strip mall:'
            : 'They want to take this card:'}
      </p>
      <div className={styles.requestFlow}>
        <RequestedAssets
          projection={projection}
          label="They take from you"
          instanceIds={request.requestedIds}
        />
        {swap ? (
          <>
            <span className={styles.requestArrow} aria-hidden="true">
              ⇄
            </span>
            <RequestedAssets
              projection={projection}
              label="You receive"
              instanceIds={request.offeredIds}
            />
          </>
        ) : null}
      </div>
      <p className={styles.requestedHint}>Your requested card is outlined in red on the table.</p>
    </div>
  );
}

function RequestedAssets({
  projection,
  label,
  instanceIds,
}: {
  projection: GameProjection;
  label: string;
  instanceIds: string[];
}) {
  return (
    <div className={styles.requestSide}>
      <span className={styles.requestLabel}>{label}</span>
      <div className={styles.requestAssets}>
        {instanceIds.map((instanceId) => (
          <div key={instanceId} className={styles.requestAsset}>
            <Card
              definitionId={projection.cards[instanceId]!}
              size="xs"
              context={label.toLowerCase()}
            />
            <b>{titleOf(projection, instanceId)}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

function progressStyle(progress: number): CSSProperties {
  return { '--turn-progress': `${Math.min(Math.max(progress, 0), 1) * 100}%` } as CSSProperties;
}

function WildcardMoves({
  projection,
  send,
}: {
  projection: GameProjection;
  send: (body: CommandBody) => void;
}) {
  const moves = legalOfType(projection, 'moveWildcard')[0];
  if (!moves) return null;
  return (
    <div className={styles.subsection}>
      <p className={styles.subheading}>Flip a wildcard (free)</p>
      {/* One row per wildcard, led by the color it is filling right now, so
          it is always clear which card a button belongs to. */}
      {moves.options.map((option) => (
        <div key={option.cardInstanceId} className={styles.flipRow}>
          <GroupChip groups={currentGroupOf(projection, option.cardInstanceId)} size="large" />
          <span className={styles.flipArrow} aria-hidden="true">
            &rarr;
          </span>
          {option.groups.map((group) => (
            <button
              key={group}
              type="button"
              className={`lf-button lf-button--small ${styles.flipButton}`}
              onClick={() =>
                send({ type: 'moveWildcard', cardInstanceId: option.cardInstanceId, group })
              }
              aria-label={`Flip this wildcard to ${describeGroups([group])}`}
            >
              <GroupChip groups={group} />
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/** A tenant from the content bank, never repeating the current suggestion. */
function randomTenant(current: string): string {
  const options = tenantNames.filter((name) => name !== current);
  return options[Math.floor(Math.random() * options.length)] ?? tenantNames[0]!;
}

/** The mall a wildcard currently sits in. */
function currentGroupOf(projection: GameProjection, instanceId: string): GroupId {
  for (const [group, mall] of Object.entries(projection.self.malls)) {
    if (mall?.cards.includes(instanceId)) return group as GroupId;
  }
  return 'black';
}

function ActionBuilder({
  projection,
  draft,
  setDraft,
  onCancel,
  onConfirm,
}: {
  projection: GameProjection;
  draft: ActionDraft;
  setDraft: (draft: ActionDraft) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { option, targets } = draft;
  const card = getCard(option.definitionId);

  const setTarget = (patch: Partial<ActionTargets>): void =>
    setDraft({ ...draft, targets: { ...targets, ...patch } });

  const needsPlayer =
    (option.targetKind === 'player' ||
      option.targetKind === 'rentGroupAndPlayer' ||
      (option.targetKind === 'completeMall' && option.playerIds.length > 0)) &&
    !targets.playerId;

  const needsGroup =
    (option.targetKind === 'rentGroup' ||
      option.targetKind === 'rentGroupAndPlayer' ||
      option.targetKind === 'completeMall') &&
    !targets.group;

  // Cards are chosen by pointing at them on the table, not from a list.
  const cardStep = targetStepFor(projection, draft);
  const ready = !cardStep && !needsPlayer && !needsGroup;

  // Only offer malls the chosen victim actually owns.
  const groupChoices =
    option.targetKind === 'completeMall' && targets.playerId
      ? option.groups.filter((g) => {
          const victim = projection.opponents.find((o) => o.id === targets.playerId);
          return victim?.completeMalls.includes(g) ?? false;
        })
      : option.groups;

  return (
    <Panel title={card.title}>
      <p className={styles.note}>{card.description}</p>

      {/* Visible at every step, so an attached modifier is never a surprise. */}
      {draft.doubles.length > 0 ? (
        <p className={styles.applied} data-testid="doubler-applied">
          Renegotiate the Rent is attached — this rent is doubled, and the pair costs two actions.
        </p>
      ) : null}

      {cardStep ? (
        <p className={styles.prompt} data-testid="target-prompt">
          {cardStep.prompt}
        </p>
      ) : needsPlayer ? (
        <Chooser label="Choose a player">
          {option.playerIds.map((id) => (
            <button
              key={id}
              type="button"
              className="lf-button lf-button--small"
              onClick={() => setTarget({ playerId: id })}
            >
              {nameOf(projection, id)}
            </button>
          ))}
        </Chooser>
      ) : needsGroup ? (
        <Chooser label={option.targetKind === 'completeMall' ? 'Choose a mall' : 'Choose a color'}>
          {groupChoices.map((group: GroupId) => (
            <button
              key={group}
              type="button"
              className="lf-button lf-button--small"
              onClick={() => setTarget({ group })}
              aria-label={describeGroups([group])}
            >
              <GroupChip groups={group} />
            </button>
          ))}
        </Chooser>
      ) : null}

      <div className={styles.row}>
        {ready ? (
          <button type="button" className="lf-button lf-button--primary" onClick={onConfirm}>
            Confirm
          </button>
        ) : null}
        <button type="button" className="lf-button lf-button--ghost" onClick={onCancel}>
          Back
        </button>
      </div>
    </Panel>
  );
}

function Chooser({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.subsection}>
      <p className={styles.subheading}>{label}</p>
      <div className={styles.row}>{children}</div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={styles.panel} aria-label="Controls" data-testid="controls">
      <h2 className={styles.title}>{title}</h2>
      {children}
    </section>
  );
}
